import { useEffect, useReducer } from 'react';
import { useFormContext } from 'react-hook-form';

import { Alert, CounterButton, RoomCompositionPanel } from './room.styles';

import RoomComponent from './room.component';
import {
	IconFeedbackWarningComponent,
	IconActionPlusComponent,
	IconsSize
} from '@vaa-component-lib/component.atom.icons';
import {
	ButtonComponent,
	ButtonColour,
	ButtonPriority,
	ButtonType,
	IconPosition,
	ButtonSize
} from '@vaa-component-lib/component.atom.button';

import {
	RoomComposition,
	updateComposition
} from '../../lib/utils/composition.service';

import { Composition } from '../../types/rooms';

interface RoomCompositionProps {
	name: string;
	value: RoomComposition[];
	roomsDisabled?: boolean;
}

const RoomCompositionComponent = ({
	name,
	value,
	roomsDisabled = false
}: RoomCompositionProps) => {
	const { setValue, getFieldState } = useFormContext();
	const initial: Composition[] = value.map((composition) => ({
		adults: composition.adults,
		childAges: composition.childAges.concat(
			[...Array(composition.infants).keys()].map(() => 1)
		)
	}));
	const [state, dispatch] = useReducer(updateComposition, initial);
	const roomLimit = 4;

	useEffect(() => {
		const comp = state.map((composition) => ({
			adults: composition.adults,
			childAges: composition.childAges.filter((age) => age !== 1),
			infants: composition.childAges.filter((age) => age === 1).length
		}));

		setValue(name, comp, {
			shouldValidate: true,
			shouldDirty: true
		});
	}, [state]);

	return (
		<div data-cy="room-composition-component">
			{getFieldState(name)?.error && (
				<Alert data-testid="room-error">
					<IconFeedbackWarningComponent size={IconsSize.Sml} />
					{getFieldState(name)?.error?.message}
				</Alert>
			)}
			<RoomCompositionPanel>
				{state.map((composition, idx) => {
					return (
						<RoomComponent
							key={idx}
							id={`room-${idx}`}
							roomIdx={idx}
							composition={composition}
							dispatch={dispatch}
							roomsDisabled={roomsDisabled}
						/>
					);
				})}
				{!roomsDisabled && (
					<CounterButton>
						<ButtonComponent
							text="Add room"
							iconChild={<IconActionPlusComponent />}
							iconPosition={IconPosition.Left}
							colour={ButtonColour.Contrast}
							priority={ButtonPriority.Secondary}
							type={ButtonType.Button}
							size={ButtonSize.Small}
							fullWidth
							onClick={(): void => dispatch({ type: 'ADD_ROOM' })}
							disabled={state.length >= roomLimit}
							data-testid="add-room-btn"
						/>
					</CounterButton>
				)}
			</RoomCompositionPanel>
		</div>
	);
};

export default RoomCompositionComponent;
